import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_ACCOUNTS_JOURNAL } from '../../../../Routing/routes';
import { bulkValidateJournals, bulkUploadJournals, clearBulkImportState } from '../../../../redux/actions/SocietyPortal/JournalAction/JournalAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import BulkImportUpload from './BulkImportUpload';
import BulkImportValidation from './BulkImportValidation';
import BulkImportResult from './BulkImportResult';

const STEPS = [
    { num: '01', label: 'UPLOAD', desc: 'Upload your file' },
    { num: '02', label: 'VALIDATE', desc: 'Review & confirm' },
    { num: '03', label: 'RESULT', desc: 'Import result' },
];

const BulkImportJournal = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = useState(0);
    const [file, setFile] = useState(null);

    const {
        bulkValidateLoading,
        bulkUploadLoading,
        bulkValidateResult,
        bulkUploadResult,
    } = useSelector(state => state.JournalReducer);

    const societyId = selectedSocietyStorer.getSocietyId();

    useEffect(() => {
        return () => {
            dispatch(clearBulkImportState());
        };
    }, [dispatch]);

    const goBack = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_JOURNAL);

    // Step 1 → Step 2: Upload file and call bulk-validate API
    const handleUploadNext = (selectedFile) => {
        setFile(selectedFile);
        dispatch(bulkValidateJournals({
            societyId,
            file: selectedFile,
            onSuccess: () => {
                setCurrentStep(1);
            },
        }));
    };

    // Step 2 → Step 3: Call bulk-upload API with the same file
    const handleValidationNext = () => {
        dispatch(bulkUploadJournals({
            societyId,
            file,
            onSuccess: () => {
                setCurrentStep(2);
            },
        }));
    };

    const handleValidationBack = () => {
        setCurrentStep(0);
    };

    const handleDone = () => {
        navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_JOURNAL);
    };

    return (
        <div className="tw-bg-gray-50 tw-min-h-screen">
            {/* Header Bar */}
            <div className="tw-bg-white tw-border-b tw-border-gray-200 tw-px-5 tw-relative" style={{ height: 60 }}>
                {/* Left - Back + Title */}
                <div className="tw-absolute tw-left-5 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-items-center tw-gap-3">
                    <button onClick={goBack} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                    </button>
                    <div className="tw-leading-tight">
                        <p className="tw-text-[10px] tw-font-semibold tw-text-blue-600 tw-uppercase tw-tracking-wider tw-m-0">Bulk Import</p>
                        <h1 className="tw-text-[15px] tw-font-bold tw-text-gray-900 tw-m-0 tw-mt-px">Journal Entries</h1>
                    </div>
                </div>

                {/* Center - Stepper */}
                <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw--translate-x-1/2 tw--translate-y-1/2 tw-flex tw-items-center">
                    {STEPS.map((step, index) => (
                        <div key={step.label} className="tw-flex tw-items-center">
                            <div className="tw-flex tw-items-center tw-gap-2.5">
                                <div style={{ width: 34, height: 34 }} className={`tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-semibold tw-flex-shrink-0 ${
                                    index < currentStep
                                        ? 'tw-bg-emerald-500 tw-text-white'
                                        : index === currentStep
                                            ? 'tw-bg-indigo-600 tw-text-white'
                                            : 'tw-bg-white tw-text-gray-400 tw-border tw-border-gray-300'
                                }`}>
                                    {index < currentStep ? (
                                        <Icon icon="mdi:check" className="tw-text-sm" />
                                    ) : (
                                        step.num
                                    )}
                                </div>
                                <div>
                                    <p className={`tw-text-xs tw-font-semibold tw-m-0 ${
                                        index <= currentStep ? 'tw-text-gray-700' : 'tw-text-gray-400'
                                    }`}>{step.label}</p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-m-0 tw-font-normal">{step.desc}</p>
                                </div>
                            </div>
                            {index < STEPS.length - 1 && (
                                <div className={`tw-w-28 tw-mx-5 tw-border-t tw-border-dashed ${
                                    index < currentStep ? 'tw-border-emerald-400' : 'tw-border-gray-300'
                                }`} />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Step Content */}
            <div className="tw-p-6">
                {currentStep === 0 && (
                    <BulkImportUpload
                        file={file}
                        onNext={handleUploadNext}
                        onCancel={goBack}
                        loading={bulkValidateLoading}
                    />
                )}
                {currentStep === 1 && (
                    <BulkImportValidation
                        validateResult={bulkValidateResult}
                        onNext={handleValidationNext}
                        onBack={handleValidationBack}
                        loading={bulkUploadLoading}
                    />
                )}
                {currentStep === 2 && (
                    <BulkImportResult
                        result={bulkUploadResult}
                        onDone={handleDone}
                    />
                )}
            </div>
        </div>
    );
};

export default BulkImportJournal;
